import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface Contact {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | { id: string; name: string; ico?: string | null } | null;
  position: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  tags: string[];
  customFields: Record<string, unknown>;
  meta?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  // Extended fields returned by API (may not be present in all contexts)
  personalEmail?: string | null;
  personalPhone?: string | null;
  department?: string | null;
  linkedinUrl?: string | null;
  websiteUrl?: string | null;
  contactType?: string | null;
  contactScope?: string | null;
  temperature?: 'cold' | 'warm' | 'hot' | null;
  source?: string | null;
  sourceDetail?: string | null;
  sourceUrl?: string | null;
  score?: number | null;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  doNotContact?: boolean;
  doNotContactReason?: string | null;
  marketingEmailConsent?: boolean;
  marketingPhoneConsent?: boolean;
  marketingSmsConsent?: boolean;
  consentGivenAt?: string | null;
  lastContactedAt?: string | null;
  lastActivityAt?: string | null;
  importBatchId?: string | null;
  _count?: {
    leads: number;
  };
  leads?: Array<{
    id: string;
    leadType: string;
    status: string;
    expectedValue: number | null;
    createdAt: string;
    project?: {
      id: string;
      name: string;
    };
    currentStage?: {
      id: string;
      name: string;
      color: string;
    };
  }>;
}

export interface ContactsResponse {
  data: Contact[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ContactFilters {
  page?: number;
  limit?: number;
  search?: string;
  projectId?: string;
}

export interface CreateContactDto {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  address?: string;
  city?: string;
  country?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateContactDto extends Partial<CreateContactDto> {}

export function useContacts(filters: ContactFilters = {}) {
  const { currentTenant } = useTenant();
  
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.search) params.set('search', filters.search);
  if (filters.projectId) params.set('projectId', filters.projectId);

  const queryString = params.toString();
  const path = `/contacts${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['contacts', filters, currentTenant?.id],
    queryFn: () => api.get<ContactsResponse>(path),
    enabled: !!currentTenant,
  });
}

export function useContact(id: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['contact', id, currentTenant?.id],
    queryFn: () => api.get<Contact>(`/contacts/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContactDto) => api.post<Contact>('/contacts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContactDto }) =>
      api.put<Contact>(`/contacts/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ── Bulk operations ──

export function useBulkTemperature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[]; temperature: string }) =>
      api.post('/contacts/bulk/temperature', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkAddTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[]; tags: string[] }) =>
      api.post('/contacts/bulk/add-tags', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkRemoveTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[]; tags: string[] }) =>
      api.post('/contacts/bulk/remove-tags', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkMoveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[]; projectId: string }) =>
      api.post('/contacts/bulk/move-project', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkArchive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[] }) =>
      api.post('/contacts/bulk/archive', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
